import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';
import { formulaVariables } from '../utils/formula';
import useBackdropClose from '../utils/useBackdropClose';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

const EMPTY_FORMULA = {
  test_field_id: '',
  test_name: '',
  unit: '',
  formula: '',
  description: '',
  decimals: 2,
  inputs: [{ key: '', label: '', test_field_id: '' }],
};

// "Makeup conductivity" -> "makeup_conductivity", the name used in the formula.
const keyFromLabel = (label) => label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, '_$1');

// What's wrong with the formula as typed, or null when it reads cleanly.
const formulaProblem = (formula, inputs) => {
  if (!formula.trim()) return null;
  try {
    const keys = inputs.map((i) => i.key).filter(Boolean);
    const unknown = formulaVariables(formula).filter((name) => !keys.includes(name));
    return unknown.length ? `${unknown.join(', ')} ${unknown.length === 1 ? "isn't" : "aren't"} defined as ${unknown.length === 1 ? 'an input' : 'inputs'} below` : null;
  } catch (error) {
    return error.message;
  }
};

const FormulaEditor = ({ formula, testFields, api, onSaved, onClose }) => {
  const isNew = !formula.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORMULA,
    ...formula,
    description: formula.description || '',
    inputs: (formula.inputs?.length ? formula.inputs : EMPTY_FORMULA.inputs).map((input) => ({
      key: input.key || '',
      label: input.label || '',
      test_field_id: input.test_field_id ? `${input.test_field_id}` : '',
      // Until the name is edited by hand, it follows the label.
      keyEdited: !!input.key,
    })),
  }));
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const backdrop = useBackdropClose(onClose);

  const set = (changes) => setForm((current) => ({ ...current, ...changes }));
  const setInput = (index, changes) => setForm((current) => ({
    ...current,
    inputs: current.inputs.map((input, i) => (i === index ? { ...input, ...changes } : input)),
  }));

  // Tests that can still become calculated: plain tests without a formula yet.
  const available = useMemo(() => testFields.filter((t) => t.type !== 'calculated' && t.type !== 'checkbox'), [testFields]);
  const sourceTests = useMemo(() => testFields.filter((t) => t.type !== 'checkbox'), [testFields]);

  const problem = formulaProblem(form.formula, form.inputs);
  const inputsComplete = form.inputs.length > 0 && form.inputs.every((i) => i.key && i.label.trim());
  const canSave = !busy && !problem && form.formula.trim() && inputsComplete
    && (!isNew || form.test_field_id || form.test_name.trim());

  const save = () => {
    setBusy('save');
    setError(null);
    api.save({
      id: form.id,
      test_field_id: form.test_field_id || undefined,
      test_name: form.test_name,
      unit: form.unit,
      formula: form.formula,
      description: form.description,
      decimals: form.decimals,
      inputs: form.inputs.map(({ key, label, test_field_id }) => ({ key, label, test_field_id: test_field_id || undefined })),
    })
      .then((json) => { onSaved(json); onClose(); })
      .catch((e) => { setError(e.message); setBusy(null); });
  };

  const remove = () => {
    if (!window.confirm(`Remove the formula from ${form.test_name}? It goes back to a typed-in test. Results already calculated keep their values.`)) return;
    setBusy('delete');
    setError(null);
    api.destroy(form.id)
      .then((json) => { onSaved(json); onClose(); })
      .catch((e) => { setError(e.message); setBusy(null); });
  };

  return (
    <div className="admin-modal-backdrop" {...backdrop} role="presentation">
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={isNew ? 'New calculated test' : `Edit ${form.test_name}`}>
        <header className="admin-modal-head">
          <div>
            <h2 className="admin-modal-title">{isNew ? 'New calculated test' : form.test_name}</h2>
            <p className="admin-modal-subtitle">The result is worked out from its inputs on every report it's on.</p>
          </div>
          <button type="button" className="admin-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </header>

        {error && <div className="admin-error">{error}</div>}

        {isNew && (
          <section className="admin-section">
            <h3 className="admin-section-title">Test</h3>
            <div className="admin-grid">
              <label className="admin-field wide">
                <span>Use an existing test, or leave this to make a new one</span>
                <select className="admin-input browser-default" value={form.test_field_id} onChange={(e) => set({ test_field_id: e.target.value })}>
                  <option value="">New test</option>
                  {available.map((t) => <option key={t.id} value={t.id}>{t.name}{t.unit ? ` (${t.unit})` : ''}</option>)}
                </select>
              </label>
              {!form.test_field_id && (
                <>
                  <label className="admin-field">
                    <span>Name</span>
                    <input className="admin-input browser-default" value={form.test_name} placeholder="e.g. Cycles of Concentration" onChange={(e) => set({ test_name: e.target.value })} />
                  </label>
                  <label className="admin-field">
                    <span>Unit (optional)</span>
                    <input className="admin-input browser-default" value={form.unit} onChange={(e) => set({ unit: e.target.value })} />
                  </label>
                </>
              )}
            </div>
          </section>
        )}

        <section className="admin-section">
          <h3 className="admin-section-title">Inputs</h3>
          <p className="admin-calc-hint">
            Each input is a value the formula uses. Its source can be set per system on a report: typed on the test,
            another test's reading, or the last report's reading. A default test here links that test on the same system.
          </p>
          {form.inputs.map((input, index) => (
            <div key={index} className="admin-calc-input-row">
              <label className="admin-field">
                <span>Label</span>
                <input
                  className="admin-input browser-default"
                  value={input.label}
                  placeholder="e.g. Makeup conductivity"
                  onChange={(e) => setInput(index, { label: e.target.value, ...(input.keyEdited ? {} : { key: keyFromLabel(e.target.value) }) })}
                />
              </label>
              <label className="admin-field">
                <span>Name in formula</span>
                <input
                  className="admin-input browser-default"
                  value={input.key}
                  onChange={(e) => setInput(index, { key: e.target.value.replace(/[^A-Za-z0-9_]/g, ''), keyEdited: true })}
                />
              </label>
              <label className="admin-field">
                <span>Default source</span>
                <select className="admin-input browser-default" value={input.test_field_id} onChange={(e) => setInput(index, { test_field_id: e.target.value })}>
                  <option value="">Typed on the test</option>
                  {sourceTests.map((t) => <option key={t.id} value={t.id}>{t.name}{t.unit ? ` (${t.unit})` : ''}</option>)}
                </select>
              </label>
              <button
                type="button"
                className="admin-icon-btn"
                aria-label={`Remove ${input.label || 'input'}`}
                disabled={form.inputs.length === 1}
                onClick={() => set({ inputs: form.inputs.filter((_, i) => i !== index) })}
              >
                <CloseIcon />
              </button>
            </div>
          ))}
          <div className="admin-actions start">
            <button type="button" className="admin-btn ghost" onClick={() => set({ inputs: [...form.inputs, { key: '', label: '', test_field_id: '' }] })}>
              <AddIcon /> Add input
            </button>
          </div>
        </section>

        <section className="admin-section">
          <h3 className="admin-section-title">Formula</h3>
          <div className="admin-grid">
            <label className="admin-field wide">
              <span>Uses the input names with + − * / and brackets</span>
              <input
                className="admin-input browser-default admin-calc-formula"
                value={form.formula}
                placeholder={form.inputs.filter((i) => i.key).map((i) => i.key).join(' / ') || 'tower_cond / makeup_cond'}
                spellCheck={false}
                onChange={(e) => set({ formula: e.target.value })}
              />
            </label>
            {problem && <div className="admin-error admin-field wide">{problem}</div>}
            <label className="admin-field">
              <span>Decimal places</span>
              <input
                className="admin-input browser-default"
                type="number"
                min="0"
                max="6"
                value={form.decimals}
                onChange={(e) => set({ decimals: e.target.value })}
              />
            </label>
            <label className="admin-field">
              <span>Description (optional)</span>
              <input className="admin-input browser-default" value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </label>
          </div>
        </section>

        <div className="admin-actions">
          {!isNew && (
            <button type="button" className="admin-btn danger" disabled={!!busy} onClick={remove}>
              {busy === 'delete' ? 'Removing…' : 'Remove formula'}
            </button>
          )}
          <button type="button" className="admin-btn primary" disabled={!canSave} onClick={save}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin dashboard: the formulas behind calculated tests.
const AdminCalculatedTests = ({ authToken, fetchCalculatedTests, saveCalculatedTest, deleteCalculatedTest }) => {
  const [data, setData] = useState(null); // { formulas, test_fields }
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);

  const api = useMemo(() => ({
    save: (formula) => saveCalculatedTest(formula, authToken),
    destroy: (id) => deleteCalculatedTest(id, authToken),
  }), [authToken, saveCalculatedTest, deleteCalculatedTest]);

  const load = useCallback(() => (
    fetchCalculatedTests(authToken)
      .then((json) => setData({ formulas: json.formulas, test_fields: json.test_fields }))
      .catch((e) => setLoadError(e.message))
  ), [fetchCalculatedTests, authToken]);

  useEffect(() => { load(); }, [load]);

  const testNames = useMemo(() => Object.fromEntries((data?.test_fields || []).map((t) => [t.id, t.name])), [data]);

  return (
    <>
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Calculated tests</h1>
          <p className="admin-page-subtitle">Tests whose result is worked out from other readings, like cycles of concentration.</p>
        </div>
        <button type="button" className="admin-btn primary" disabled={!data} onClick={() => setEditing({ ...EMPTY_FORMULA })}>
          <AddIcon /> New calculated test
        </button>
      </header>

      {loadError && <div className="admin-error">{loadError}</div>}
      {!data && !loadError && <p className="admin-empty">Loading calculated tests…</p>}
      {data && data.formulas.length === 0 && <p className="admin-empty">No calculated tests yet.</p>}

      {data && data.formulas.length > 0 && (
        <ul className="admin-user-list">
          {data.formulas.map((f) => (
            <li key={f.id}>
              <button type="button" className="admin-user-row" onClick={() => setEditing(f)}>
                <div className="admin-user-main">
                  <span className="admin-user-name">{f.test_name}</span>
                  <span className="admin-user-meta admin-calc-formula">{f.formula || 'No formula yet'}</span>
                </div>
                <div className="admin-user-link">
                  {f.inputs.map((i) => (i.test_field_id ? `${i.label} (${testNames[i.test_field_id] || 'test'})` : i.label)).join(' · ')}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <FormulaEditor
          key={editing.id || 'new'}
          formula={editing}
          testFields={data?.test_fields || []}
          api={api}
          onSaved={(json) => setData({ formulas: json.formulas, test_fields: json.test_fields })}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};

function mapStateToProps(state) {
  return { authToken: state.authToken };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminCalculatedTests);
